import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Instagram, MessageCircle, Linkedin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import map_img from '@/assets/map.webp';
const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const goSection = (hash: string) => {
    if (location.pathname !== '/') navigate(`/${hash}`);
    else {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      else window.location.hash = hash;
    }
  };
  return (
    <footer className="bg-steel-dark text-white">
      {/* Main Footer Content */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <h3 className="text-xl font-bold text-copper mb-4">
                الکترو موتورسازی الکتریک
              </h3>
              <p className="text-white/80 leading-relaxed">
                پیشرو در تولید الکترو موتورهای الکتریکی، سیم‌پیچی و آرمیچر با بیش از
                20 سال تجربه در خدمت صنایع کشور
              </p>
              <div className="flex space-x-4 space-x-reverse pt-4">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="اینستاگرام کیا الکتروموتور"
                  title="اینستاگرام کیا الکتروموتور"
                  className="text-white hover:text-copper hover:bg-white/10"
                >
                  <Instagram className="w-5 h-5" />
                  <span className="sr-only">اینستاگرام کیا الکتروموتور</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="پیام‌رسان کیا الکتروموتور"
                  title="پیام‌رسان کیا الکتروموتور"
                  className="text-white hover:text-copper hover:bg-white/10"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span className="sr-only">پیام‌رسان کیا الکتروموتور</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="لینکدین کیا الکتروموتور"
                  title="لینکدین کیا الکتروموتور"
                  className="text-white hover:text-copper hover:bg-white/10"
                >
                  <Linkedin className="w-5 h-5" />
                  <span className="sr-only">لینکدین کیا الکتروموتور</span>
                </Button>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-semibold text-copper mb-4">
                لینک‌های سریع
              </h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-white/80 hover:text-copper transition-colors duration-200">صفحه اصلی</Link></li>
                <li><Link to="/products" className="text-white/80 hover:text-copper transition-colors duration-200">محصولات</Link></li>
                <li><button onClick={() => goSection('#about')} className="text-white/80 hover:text-copper transition-colors duration-200">درباره ما</button></li>
                <li><button onClick={() => goSection('#customers')} className="text-white/80 hover:text-copper transition-colors duration-200">مشتریان</button></li>
                <li><Link to="/blog" className="text-white/80 hover:text-copper transition-colors duration-200">مقالات</Link></li>
                <li><Link to="/contact" className="text-white/80 hover:text-copper transition-colors duration-200">تماس با ما</Link></li>
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-semibold text-copper mb-4">
                لوکیشن ما
              </h4>
              <a aria-label="مشاهده نشانی کیا الکتروموتور روی نقشه" style={{cursor:'pointer'}} href="https://www.google.com/maps/place//data=!4m2!3m1!1s0x3f8d99a0602436e5:0x904aed995d46bb83?sa=X&ved=1t:8290&ictx=111" target='_blank' rel='noreferrer' ><img alt="نقشه نشانی کارخانه کیا الکتروموتور" width={240} height={120} loading="lazy" decoding="async" className='rounded-md' src={map_img} /></a>

            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h4 className="text-lg font-semibold text-copper mb-4">
                اطلاعات تماس
              </h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 space-x-reverse">
                  <Phone className="w-4 h-4 text-copper flex-shrink-0" />
                  <span className="text-white/80">021-91009883</span>
                  <span className="text-white/80">021-65028992</span>
                </div>
                <div className="flex items-center space-x-3 space-x-reverse">
                  <Mail className="w-4 h-4 text-copper flex-shrink-0" />
                  <span className="text-white/80">info@kiaelectromotor.com</span>
                </div>
                <div className="flex items-start space-x-3 space-x-reverse">
                  <MapPin className="w-4 h-4 text-copper flex-shrink-0 mt-1" />
                  <span className="text-white/80 text-sm leading-relaxed">
                    صفادشت، شهرک صنعتی، خیابان دهم غربی  پلاک 164         </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Newsletter Section 
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="border-t border-white/10 py-8"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto text-center">
            <h4 className="text-lg font-semibold text-copper mb-4">
              عضویت در خبرنامه
            </h4>
            <div className="flex space-x-2 space-x-reverse">
              <Input 
                placeholder="ایمیل خود را وارد کنید"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/60 flex-1"
              />
              <Button 
                className="bg-copper hover:bg-copper-dark text-white"
              >
                عضویت
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
*/}
      {/* Bottom Bar */}
      <div className="border-t border-white/10 py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-white/60 text-sm">
              © 1404 شرکت کیا الکتروموتور پارت. تمامی حقوق محفوظ است.
            </div>
            <div className="flex space-x-6 space-x-reverse text-sm">
              <Link to="/contact" className="text-white/60 hover:text-copper transition-colors duration-200">تماس با ما</Link>
              <Link to="/blog" className="text-white/60 hover:text-copper transition-colors duration-200">مقالات</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
